import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Dialog, IconButton, Typography, useTheme } from '@mui/material'

import noParticipationImg from '../../../assets/no-participation.svg'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import { formatDate, Icons } from '../../../utils/utilities'
import CountCard from '../../common/CountCard'
import writeXlsxFile from 'write-excel-file'
import api from '../../../service/api'
import ConfirmDialog from '../../common/ConfirmDialog'
import { toast } from 'react-toastify'
import { useUserStore } from '../../../store/user.store'
import SimbaAIForFaculty from '../../ai-mentor/SimbaAIForFaculty'
import { useSearchParams } from 'react-router-dom'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import CustomTooltip from '../../common/CustomTooltip'

const columns = [
    { id: 1, name: "Sl.No.", field: "index", index: true, width: "80px", maxWidth: "100px", align: "center" },
    { id: 1, name: "Name", field: "student_name", filterable: true, sortable: true, width: "80px", },
    { id: 1, name: "AUID", field: "student_id", filterable: true, sortable: true, width: "100px", },
    { id: 1, name: "Max Score", field: "max_score", width: "80px", align: "center" },
    { id: 1, name: "Best Score", sortable: true, filterable: true, field: "best_score", width: "80px", align: "center" },
    { id: 1, name: "Attempts", sortable: true, filterable: true, field: "number_of_attempts", width: "80px", align: "center" },
]


const participationReport = async ( data, title ) => {
    let rows = [[
        { fontWeight: 'bold', value: "Sl.No." },
        { value: "Student name", fontWeight: 'bold' },
        { fontWeight: 'bold', value: "AUID" },
        { value: "Max marks", fontWeight: 'bold' },
        { value: "Best Score", fontWeight: 'bold' },
        { value: "Attempts", fontWeight: 'bold' }
    ]]
    for ( let i = 0; i < data.length; i++ ) {
        const participation = data[i]
        let row = []
        row.push( { value: i + 1, width: "200px", type: Number } )
        row.push( { value: participation?.student_name, width: "200px", type: String } )
        row.push( { value: participation.student_id, width: "200px", type: String } )
        row.push( { value: +participation.max_score, width: "200px", type: Number } )
        row.push( { value: +participation.best_score, width: "200px", type: Number } )
        row.push( { value: +participation.number_of_attempts, width: "200px", type: Number } )
        rows.push( row )
    }
    const excelData = await writeXlsxFile( rows, { buffer: true } )
    const blob = new Blob( [excelData] )
    const a = document.createElement( 'a' )
    a.download = `${title}.xlsx` || `practice-session-report.xlsx`
    a.href = window.URL.createObjectURL( blob )
    a.click()
}

const getAccessGroupDetails = groupDetails => {
    let data = ""
    if ( groupDetails.entity === 'batch' ) {
        data += `${groupDetails.course_name_short} ${groupDetails.ac_year}, Year: ${groupDetails.current_year}`
        if ( groupDetails.current_sem ) {
            data += `, Sem: ${groupDetails.current_sem}`
        }
        data += `, Batch: ${groupDetails.batch_name}`
        return data
    } else {
        data += `${groupDetails.course_short_name}-${groupDetails.course_branch_short_name} ${groupDetails.ac_year}, Year: ${groupDetails.current_year}`
        if ( groupDetails.current_sem ) {
            data += `, Sem: ${groupDetails.current_sem}`
        }
        data += `, Section: ${groupDetails.section_short_name}`
        return data
    }
}

const AuditPracticeSessionViewer = ( { refreshList, open, onClose, practiceSessionId } ) => {

    const [deleteConfirmationDialogState, setDeleteConfirmationDialogState] = useState( false )
    const [deleting, setDeleting] = useState( false )
    const [practiceSession, setPracticeSession] = useState( null )
    const [participants, setParticipants] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { border, layout, palette } = useTheme()

    const UserStore = useUserStore()
    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }


    const startQuiz = async () => {
        setSearchParamsValues( { simbaAIOpen: 'true', practiceQuiz: practiceSession._id, simbaAIMode: "practice" } )

        SimbaAIStore.setView( "loading" )
        const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
        if ( success ) {
            SimbaAIStore.setView( "question", "left", false )
        } else
            SimbaAIStore.setView( "practice-session-details-fetch-error", "right", false )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { practice_session, participants } } } = await api.simbaAI.fetchPracticeSession( practiceSessionId )

            setPracticeSession( practice_session )
            setParticipants( participants || [] )
            setError( null )
        } catch ( err ) {
            setError( { message: err?.response?.data?.message, code: err?.response?.status || 500 } )
        } finally {
            setLoading( false )
        }
    }, [practiceSessionId] )

    const deletePracticeSession = async () => {
        try {
            setDeleting( true )
            await api.simbaAI.deletePracticeSession( practiceSession._id )
            if ( refreshList && typeof refreshList === 'function' )
                refreshList()
            onClose()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the feature permission rules." )
        } finally {
            setDeleting( false )
        }
    }

    useEffect( () => {
        getData()
    }, [getData] )

    const aggregatedParticipants = Object.values( participants.reduce( ( acc, current ) => {
        const id = current.student_id

        if ( !acc[id] ) {
            // Initialize the student record if it doesn't exist
            acc[id] = {
                student_name: current.student_name,
                student_id: current.student_id,
                best_score: current.score,
                max_score: current.max_score,
                number_of_attempts: 1
            }
        } else {
            // Update existing record
            acc[id].number_of_attempts += 1
            // Update best_score if the current one is higher
            if ( current.score > acc[id].best_score ) {
                acc[id].best_score = current.score
            }
        }

        return acc
    }, {} ) )

    return (
        <Dialog open={open} onClose={onClose} >
            <Box>

                {!loading && practiceSession && <ConfirmDialog waiting={deleting} actionName='Delete' message={`All the associated attempt details will be deleted!`} enableSecondaryConfirmation confirmAction={deletePracticeSession} cancelAction={() => setDeleteConfirmationDialogState( false )} status={deleteConfirmationDialogState} />}


                {!loading && !error && <Box padding="20px" display="flex" flexDirection="column" gap={layout.sectionsGap}>

                    {/* Basic Details */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap="20px">
                        <Box display="flex" flexDirection="column" gap="2px" position="relative"  >
                            <Typography variant="h1" fontWeight="600" component="h2" fontSize="16px">{practiceSession.title}</Typography>
                            <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{practiceSession?.subject?.subject_name} practice session for the topics covered for from {formatDate( practiceSession.date_range_start )} - {formatDate( practiceSession.date_range_end )}</Typography>
                        </Box>

                        <IconButton size='small' onClick={onClose}>
                            {Icons.default.CloseIcon}
                        </IconButton>

                    </Box>

                    <CustomTooltip disableInteractive title="See how topics are presented to students during practice">
                        <Button onClick={startQuiz} sx={{ flexGrow: { xs: "1", sm: "initial" } }} size='small' color='primary' disableElevation startIcon={Icons.default.QuizModule} variant='contained'>
                            Student View Preview
                        </Button>
                    </CustomTooltip>

                    {/* Danger zone; Delete Practice Session */}
                    {["admin", "subadmin"].includes( UserStore.getUser.user_role?.toLowerCase() ) && <Box borderRadius="10px" gap="20px" padding="10px" bgcolor={palette.error.light + "22"} display="flex" flexDirection="column" alignItems="flex-start" flexWrap="wrap" justifyContent="space-between" border={`1px solid ${palette.error.main}`} >
                        <Box>
                            <Typography color='error' display="flex" alignItems="center" gap="5px" variant='h6' fontSize="16px" component="h3"> {Icons.default.Warning} Delete {practiceSession.title}</Typography>
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2' component="p"> Any and all associated attempts will be deleted along with the practice session.</Typography>
                        </Box>
                        <Button color='error' variant='contained' disableElevation size='small' onClick={() => setDeleteConfirmationDialogState( true )} startIcon={Icons.default.DeleteForever}>Delete Practice Session</Button>
                    </Box>}

                    {/* Stats */}
                    <Box display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(auto-fit,minmax(200px,1fr))", xs: "repeat(2,1fr)" }}>
                        <CountCard loading={loading} label='Subject' icon={Icons.default.Subjects} value={`${practiceSession?.subject?.subject_name_short} (${practiceSession?.subject?.subject_code})`} />
                        <CountCard loading={loading} label='Practices Made' icon={Icons.default.Click} value={participants.length} />
                        <CountCard loading={loading} label='Participants' icon={Icons.default.Group} value={Object.keys( participants.reduce( ( acc, attempt ) => {
                            if ( !acc[attempt.student_id] ) acc[attempt.student_id] = true
                            return acc
                        }, {} ) ).length} />
                        <CountCard loading={loading} label='Total Questions' icon={Icons.default.QuestionMark} value={participants.length * ( participants[0]?.max_score || 0 )} />
                    </Box>

                    {/* Topics */}
                    <Box display="flex" flexDirection="column" gap="5px"  >
                        {Object.keys( practiceSession.modules_and_topics ).map( ( module, modIndex ) => (
                            <Box border={border[1]} borderRadius={layout.borderRadius} key={module} gap="5px">
                                <Box padding="5px 10px" bgcolor={palette.contentBg} borderBottom={border[1]} >
                                    <Typography variant='subtitle2' lineHeight={1.2} color='textSecondary' fontSize="12px"> {practiceSession.subject.subject_name}, {module}</Typography>
                                    <Typography variant='subtitle2' component="p" fontSize="14px"> {practiceSession.modules_and_topics[module]?.name}</Typography>
                                </Box>
                                <Box padding="5px" display="flex" flexWrap="wrap" gap="5px">
                                    {practiceSession.modules_and_topics[module].topics.map( ( topic, topicIndex ) => (
                                        <Box border={border[1]} padding="5px 10px" borderRadius={layout.borderRadius} key={topic} display="flex" alignItems="center" gap="5px">
                                            <Typography variant='subtitle2' fontSize="12px"  >{topic}</Typography>
                                        </Box>
                                    ) )}
                                </Box>
                            </Box>
                        ) )}
                    </Box>

                    {/* Access Groups */}
                    <Box >
                        <Typography variant='subtitle2' component="h3">Batches and Sections Assigned</Typography>
                        <Box display="flex" gap={layout.cardsGap}>
                            {practiceSession.access_groups.map( ag => {
                                return (
                                    <Box key={ag.id} bgcolor={palette.contentBg} padding="5px 10px" borderRadius={layout.borderRadius} border={border[1]}>
                                        <Typography variant='subtitle2' display="block" textTransform="uppercase" color='secondary' noWrap fontSize="10px" >{ag.entity}</Typography>
                                        <Typography variant='subtitle2' display="block" color='textSecondary' noWrap fontSize="12px" >{getAccessGroupDetails( ag )}</Typography>
                                    </Box>
                                )
                            } )}
                        </Box>
                    </Box>

                    {/* Participants */}
                    <Box>
                        <Typography variant='subtitle2' component="h3">Participants List</Typography>
                        {aggregatedParticipants?.length > 0 && <CustomTable
                            customActions={[{
                                type: "button",
                                label: "Export",
                                startIcon: Icons.default.Upload,
                                variant: "outlined",
                                color: "greyed",
                                onClick: rows => participationReport( rows, `${practiceSession.subject.subject_name}-${practiceSession?.title}` )
                            }]}
                            columns={columns}
                            data={aggregatedParticipants}
                            containerStyles={{ boxShadow: "none", maxHeight: "450px" }}
                        />}
                        {aggregatedParticipants?.length === 0 && <Box padding="20px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} display="flex" flexDirection="column" gap="10px" alignItems="center" justifyContent="center"  >
                            <Box component="img" src={noParticipationImg} width="180px" />
                            <Typography color='textSecondary' variant='subtitle2'>This practice session has no participants.</Typography>
                        </Box>}
                    </Box>

                </Box>}
                {loading && !error && <DataLoadingSpinner waitingMessage='Loading practice session details...' />}
                {error && <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                    <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} loading={loading} />
                </Box>}
            </Box>

            {/* Sample AI Response Checker for Faculty */}
            <SimbaAIForFaculty />

        </Dialog>
    )
}

export default AuditPracticeSessionViewer
